import React, { useState, useEffect, useRef, memo, useLayoutEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

import InfoCircleFilled from "@ant-design/icons/InfoCircleFilled";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CachedIcon from "@mui/icons-material/Cached";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";

import { ButtonUI, ConfirmPopup } from "../../../Common";
import MultiSelectDropDown from "../../../Common/MultiSelectDropDown";
import {
  setPrevState,
  setStorageValue,
  getStoredValue,
  showLastRunText,
  convertTimezone,
} from "../../../../util/common";
import { JOB_TYPES } from "../utils/JobData";
import { showToast, hideToast } from "../../../../store/actions";
import WorkspaceService from "../../../../service/workspace";
import JobHistory from "../JobHistory/JobHistory";
import DataLoadErrorLog from "../DataModeling/DataLoad/DataLoadErrorLog";
import ProgressBar from "../../../Common/LinearProgress";
import { navScheduledJobs } from "../../../kmk/AppData";
import { DATA_SOURCE } from "../../../../util/Data/DataModeling";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import MultiSelect from "../../../../util/multiselect";
import KMKTable from "../../../Common/Table/KMKTable";
import useWindowResize from "../../../../hooks/useWindowsReszie";
import ErrorLog from "../ErrorLog";

const useStyles = makeStyles()({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
  },
  tableButton: {
    textTransform: "inherit !important",
    padding: "4px 12px",
    marginLeft: "10px",
  },
  IconHolder: {
    borderRadius: "50px",
    margin: "0",
    padding: "5px 5px",
    fontSize: "19px",
  },
});

const DATA_MODELING_JOBS = [1, 4, 5, 6];
const NON_MODELING_JOBS = [2, 3, 7, 8, 9];

const PopupTitle = {
  1: { run: "Load Data to Data Warehouse", error: "Data Load Error Log" },
  4: { run: "Run Data Quality Job", error: "Data Quality Error Log" },
  5: { run: "Run Data Mastering Job", error: "Data Mastering Error Log" },
  6: { run: "Run Data Deduplication Job", error: "Data Deduplication Error Log" },
  2: { error: "Data Exchange Error Log" },
  3: { error: "Data Transform Error Log" },
  7: { error: "Notebook Error Log" },
  8: { error: "Routing Error Log" },
  9: { error: "Notification Error Log" },
};

const handleLastRunText = (status, statusText, lastRunTimeInMS, averageRunTimeInMS, StartTime) => {
  if (["success", "error", "failed", "terminating", "terminated", "warning"].includes(status)) {
    return showLastRunText(status, statusText, lastRunTimeInMS, convertTimezone(StartTime || null));
  } else if (["running"].includes(status) && averageRunTimeInMS !== null) {
    return <ProgressBar StartTime={StartTime} averageRunTimeInMS={averageRunTimeInMS} />;
  } else {
    return "N/A";
  }
};

const JobList = (props) => {
  const { scheduleState, setScheduleState, jobList, setJobList, handleJobsMenuOpen, selectedPipelines } = props;

  const { Workspace_id } = useParams();

  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { height: tableHeight } = useWindowResize(200);
  const allowAutoUpdate = useRef(true);

  const { workspace } = useSelector((state) => state.workspace);
  const { User_id } = useSelector((state) => state.user.currentUser);

  const { accessRole, IsDataVaultEnabled } = workspace;

  const [search, setSearch] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Name");

  const [selectedType, setSelectedType] = useState([]);
  const [runNowDisable, setRunNowDisable] = useState(false);

  const [errorLogPopup, setErrorLogPopup] = useState({
    open: false,
    JobType: null,
    errors: "",
  });

  const [historyPopup, setHistoryPopup] = useState({
    open: false,
    Job_id: null,
    JobType: null,
    JobName: "",
  });

  const [openConfirm, setOpenConfirm] = useState({
    open: false,
    Job_id: 0,
    JobType: "",
  });

  const [openStopConfirm, setOpenStopConfirm] = useState({
    open: false,
    Job_id: 0,
    JobType: "",
    JobName: "",
  });

  const [conflictConfirm, setConflictConfirm] = useState({
    open: false,
    Job_id: 0,
    JobType: "",
    Title: <></>,
    message: <></>,
  });

  const listingRef = useRef({ search, selectedType, selectedPipelines, orderBy, order });

  const runningJobIds = useMemo(
    () => jobList.filter((job) => job.status === "running" || job.status === "terminating").map((job) => job.Job_id),
    [jobList]
  );

  const handleLocalStorageFilter = (key, value) => {
    setStorageValue(`${User_id}_PipelineFilters`, {}, (storedValue) => {
      const updatedFilters = {
        ...storedValue,
        [Workspace_id]: { ...storedValue[Workspace_id], [key]: value },
      };

      return updatedFilters;
    });
  };

  const getAllJobs = useCallback(
    async (runningJobIds = []) => {
      if (!runningJobIds?.length) {
        allowAutoUpdate.current = false;
        setPrevState(setScheduleState, { blockLoading: true });
      }

      const wsService = new WorkspaceService();
      const { data, success } = await wsService.getAllJobs(Workspace_id, {
        search: listingRef.current.search,
        orderBy: listingRef.current.orderBy,
        order: listingRef.current.order,
        jobTypeList: listingRef.current.selectedType.length ? listingRef.current.selectedType : selectedType,
        pipelineList: listingRef.current.selectedPipelines.length
          ? listingRef.current.selectedPipelines
          : selectedPipelines,
        jobIds: runningJobIds,
      });

      if (success && runningJobIds?.length) {
        setJobList((prevJobList) =>
          prevJobList.map((job) =>
            runningJobIds.includes(job.id) ? data.jobs.find((newJob) => newJob.id === job.id) || job : job
          )
        );
      } else if (success) setJobList(data.jobs);

      if (!runningJobIds?.length) {
        setPrevState(setScheduleState, { blockLoading: false });
        allowAutoUpdate.current = true;
      }

      return { data, success };
    },
    [Workspace_id, selectedPipelines, selectedType, setJobList, setScheduleState]
  );

  const updateListingRefObj = async (options) => {
    listingRef.current = { search, selectedType, selectedPipelines, orderBy, order, ...options };
    getAllJobs();
  };

  const handleSorting = (property) => {
    setOrderBy((prevProperty) => {
      setOrder((prevOrder) => {
        const isAsc = prevProperty === property && prevOrder === "asc";
        const reverseOrder = isAsc ? "desc" : "asc";

        updateListingRefObj({ orderBy: property, order: reverseOrder });
        handleLocalStorageFilter("sortedOn", { orderBy: property, order: reverseOrder });

        return reverseOrder;
      });
      return property;
    });
  };

  const addJob = useCallback(
    async (JobType, Job_id = "") => {
      let JobPath = JOB_TYPES.find((job) => job.type === JobType)?.path;
      navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}/${JobPath}/${Job_id || ""}`);
    },
    [Workspace_id, navigate]
  );

  const handleRowClick = useCallback(
    (record) => {
      if ([1, 2].includes(accessRole)) addJob(record.Type, record.Job_id);
    },
    [accessRole, addJob]
  );

  // update the job from the JobList based on single response
  const updateJobList = useCallback(
    (responseData) => {
      const updatedJobs = jobList.map((job) => {
        if (parseInt(job.Job_id) === parseInt(responseData.Job_id)) return { ...job, ...responseData };
        return job;
      });
      setJobList(updatedJobs);
    },
    [jobList, setJobList]
  );

  const getJobStatus = useCallback(
    async (Job_id) => {
      allowAutoUpdate.current = false;
      dispatch(hideToast());
      setPrevState(setScheduleState, { blockLoading: true });

      const wsService = new WorkspaceService();
      const response = await wsService.getJobStatus(Workspace_id, Job_id);
      if (response?.success) updateJobList(response.data);

      setPrevState(setScheduleState, { blockLoading: false });
      allowAutoUpdate.current = true;
    },
    [Workspace_id, dispatch, setScheduleState, updateJobList]
  );

  const handleConflict = (JobType, data) =>
    setConflictConfirm({
      open: true,
      Title: (
        <Box sx={{ m: 0, p: 0, display: "flex", alignItems: "center" }}>
          <ReportProblemIcon color="warning" sx={{ mr: "8px" }} />
          <Typography>Attention</Typography>
        </Box>
      ),
      message: (
        <>
          <Typography variant="body2">
            {JobType === 1 &&
              `One or more running jobs have same ${DATA_SOURCE.plural.toLowerCase()} selected and/or mapped entities.`}
            {JobType === 4 && `One or more running jobs have same ${DATA_SOURCE.plural.toLowerCase()} selected.`}
            {[5, 6].includes(JobType) &&
              `One or more running jobs have same data entities selected and/or has a mapped ${DATA_SOURCE.singular.toLowerCase()} in another job.`}
          </Typography>
          {data.jobs?.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                Conflicting Jobs
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.jobs.map((job, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={job}>
                      <ListItemText primary={`${job}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          {data.dataSources.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                {`Conflicting ${DATA_SOURCE.plural}`}
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.dataSources.map((ds, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={ds}>
                      <ListItemText primary={`${ds}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          {data.entities.length !== 0 && (
            <>
              <Typography variant="body2" mt={2}>
                Conflicting Entities
              </Typography>
              <div style={{ maxHeight: "300px", overflowY: "auto", marginTop: "10px" }}>
                <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                  {data.entities.map((entity, i) => (
                    <ListItem sx={{ display: "list-item", p: 0 }} key={entity}>
                      <ListItemText primary={`${entity}`} />
                    </ListItem>
                  ))}
                </List>
              </div>
            </>
          )}
          <Typography variant="body2" mt={2}>
            Running this job concurrently may lead to inconsistent output or potential data loss.
          </Typography>
        </>
      ),
    });

  const handleJobRun = useCallback(
    async (Job_id, JobType, validate = true) => {
      allowAutoUpdate.current = false;
      if (DATA_MODELING_JOBS.includes(JobType)) setPrevState(setScheduleState, { loadingForConfirm: true });
      else setPrevState(setScheduleState, { blockLoading: true });
      setRunNowDisable(true);

      const wsService = new WorkspaceService();

      let response = null;
      if (JobType === 2) response = await wsService.runDataExchangeJob(Workspace_id, Job_id);
      else if (JobType === 3) response = await wsService.runDataTranformJob(Workspace_id, Job_id);
      else if (JobType === 7) response = await wsService.runNotebookJob(Workspace_id, Job_id);
      else if (JobType === 8) response = await wsService.runRoutingJob(Workspace_id, Job_id);
      else if (JobType === 9) response = await wsService.runNotificationJob(Workspace_id, Job_id);
      else response = await wsService.dataloadJob("init", Workspace_id, Job_id, JobType, validate);

      if (response.success) {
        if (response.data?.conflict) handleConflict(JobType, response.data);
        else {
          updateJobList(response.data);

          dispatch(
            showToast({
              message: response.message,
              type: "success",
            })
          );
        }
      }

      setRunNowDisable(false);
      if (DATA_MODELING_JOBS.includes(JobType)) {
        if (validate) setPrevState(setOpenConfirm, { open: false });
        else setPrevState(setConflictConfirm, { open: false });
        setPrevState(setScheduleState, { loadingForConfirm: false });
      } else setPrevState(setScheduleState, { blockLoading: false });
      allowAutoUpdate.current = true;
    },
    [Workspace_id, dispatch, setScheduleState, updateJobList]
  );

  const handleStopJobExecution = async (Job_id, JobType) => {
    allowAutoUpdate.current = false;
    if (DATA_MODELING_JOBS.includes(JobType)) setPrevState(setScheduleState, { loadingForConfirm: true });
    else setPrevState(setScheduleState, { blockLoading: true });
    setRunNowDisable(true);

    const wsService = new WorkspaceService();
    let response = await wsService.stopJobExecutionById(Workspace_id, Job_id, JobType);

    if (response.success) {
      updateJobList(response.data);
      dispatch(
        showToast({
          message: response.message,
          type: "success",
        })
      );
    }

    setRunNowDisable(false);
    if (DATA_MODELING_JOBS.includes(JobType)) setPrevState(setScheduleState, { loadingForConfirm: false });
    else setPrevState(setScheduleState, { blockLoading: false });
    setPrevState(setOpenStopConfirm, { open: false });
    allowAutoUpdate.current = true;
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      updateListingRefObj({ search: e.target.value });
      handleLocalStorageFilter("search", e.target.value);
    }
  };

  const handleClearSearch = () => {
    setSearch("");
    updateListingRefObj({ search: "" });
    handleLocalStorageFilter("search", "");
  };

  const handleTypeFilter = (selectionList) => {
    setSelectedType(selectionList);
    updateListingRefObj({ selectedType: selectionList });
    handleLocalStorageFilter("selectedType", selectionList);
  };

  const columns = useMemo(
    () => [
      {
        id: "Name",
        name: "NAME",
        style: { minWidth: 180, wordBreak: "break-word" },
        align: "left",
        sortable: true,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => record?.Name ?? "",
      },
      {
        id: "Type",
        name: "TYPE",
        style: { minWidth: 150, wordBreak: "break-word" },
        align: "left",
        sortable: true,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => JOB_TYPES.find((job) => job.type === record.Type)?.Name ?? "-",
      },
      ...(IsDataVaultEnabled
        ? [
            {
              id: "DataPipelineName",
              name: "PIPELINE",
              style: { minWidth: 180, wordBreak: "break-word" },
              align: "left",
              sortable: true,
              kmkEnabled: false,
              renderHeadCell: (name) => name,
              renderValueCell: (val, record, index) => record?.DataPipelineName ?? "",
            },
          ]
        : []),
      {
        id: "Schedule",
        name: `SCHEDULE`,
        style: { minWidth: 180, wordBreak: "break-word" },
        align: "left",
        sortable: true,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          if (!scheduleState.loading) {
            if (record.Enabled) {
              if (record.nextRun)
                return `${record.Schedule ? record.Schedule : ""}\n\rNext Job run time: ${convertTimezone(
                  record.nextRun
                )}`;
              else return "";
            } else return "Disabled or Not scheduled";
          }
          return "";
        },
      },
      {
        id: "status",
        name: "STATUS",
        style: { minWidth: 120, wordBreak: "break-word" },
        align: "left",
        sortable: false,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <>
            {!record.status && "N/A"}
            {["ready", "running", "draft"].includes(record.status) && record.statusText}
            {["terminating"].includes(record.status) && "Running"}
            {record.status && !["ready", "running", "draft", "terminating"].includes(record.status) && "Ready"}
          </>
        ),
      },
      {
        id: "lastRunTimeInMS",
        name: `LAST RUN`,
        style: { minWidth: 200, wordBreak: "break-word" },
        align: "left",
        sortable: true,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <>
            {["error", "failed", "warning"].includes(record.status) && (
              <Tooltip title={record.statusText}>
                <IconButton
                  size="small"
                  color={["error", "failed"].includes(record.status) ? "error" : "warning"}
                  sx={{ mr: "5px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setErrorLogPopup({
                      open: true,
                      errors: record.errorStack,
                      JobType: record.Type,
                    });
                  }}
                >
                  <InfoCircleFilled />
                </IconButton>
              </Tooltip>
            )}
            {handleLastRunText(
              record.status,
              record.statusText,
              record.lastRunTimeInMS,
              record.averageRunTimeInMS,
              record.StartTime
            )}
          </>
        ),
      },
      {
        id: "Action",
        name: "ACTION",
        style: { minWidth: 120, wordBreak: "break-word" },
        align: "right",
        sortable: false,
        kmkEnabled: true,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <>
            <Tooltip title="History">
              <span
                style={
                  [3].includes(accessRole)
                    ? {
                        paddingRight: "25px",
                      }
                    : {}
                }
              >
                <IconButton
                  className={classes.IconHolder}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!(record.status && ["draft"].includes(record.status)))
                      setHistoryPopup({
                        open: true,
                        Job_id: record.Job_id,
                        JobType: record.Type,
                        JobName: record.Name,
                      });
                  }}
                  sx={
                    record.status && ["draft"].includes(record.status)
                      ? { color: "rgba(0, 0, 0, 0.26)" }
                      : { color: "#0288D1" }
                  }
                >
                  <HistoryIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            {[1, 2].includes(accessRole) ? (
              <>
                <Tooltip title="Refresh" placement="top-start">
                  <span>
                    <IconButton
                      variant="contained"
                      color="info"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!(record.status && ["draft"].includes(record.status))) getJobStatus(record.Job_id);
                      }}
                      className={classes.IconHolder}
                      sx={
                        record.status && ["draft"].includes(record.status)
                          ? { color: "rgba(0, 0, 0, 0.26)" }
                          : { color: "#1B5E20" }
                      }
                    >
                      <CachedIcon fontSize="inherit" />
                    </IconButton>
                  </span>
                </Tooltip>
                {/* Rather then disabeling the button, just changing color of it and restrecting it so that entire record can be clickable */}
                {record.status === "running" || record.status === "terminating" ? (
                  <Tooltip title="Stop">
                    <span>
                      <IconButton
                        variant="contained"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (record.status === "terminating") return;
                          setOpenStopConfirm({
                            open: true,
                            Job_id: record.Job_id,
                            JobType: record.Type,
                            JobName: record.Name,
                          });
                        }}
                        className={classes.IconHolder}
                        sx={record.status === "terminating" ? { color: "rgba(0, 0, 0, 0.26)" } : {}}
                      >
                        <StopCircleOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title="Run Now">
                    <span>
                      <IconButton
                        variant="contained"
                        color="info"
                        onClick={(e) => {
                          e.stopPropagation();

                          if (["running", "draft"].includes(record.status) || runNowDisable) return;

                          if (DATA_MODELING_JOBS.includes(record.Type))
                            setOpenConfirm({ open: true, Job_id: record.Job_id, JobType: record.Type });
                          else if (NON_MODELING_JOBS.includes(record.Type)) handleJobRun(record.Job_id, record.Type);
                        }}
                        className={classes.IconHolder}
                        sx={
                          ["running", "draft"].includes(record?.status) || runNowDisable
                            ? { color: "rgba(0, 0, 0, 0.26)" }
                            : {}
                        }
                      >
                        <PlayCircleOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            ) : null}
          </>
        ),
      },
    ],
    [
      IsDataVaultEnabled,
      accessRole,
      classes.IconHolder,
      getJobStatus,
      handleJobRun,
      runNowDisable,
      scheduleState.loading,
    ]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!allowAutoUpdate.current || runningJobIds.length === 0) return;

      const { data, success } = await getAllJobs(runningJobIds);

      if (success) {
        //Creating job_id => index map
        const jobIndexMap = {};
        jobList.forEach((item, index) => (jobIndexMap[item.Job_id] = index));

        const updatedJobList = jobList.map((job) => {
          const updatedJob = data.jobs.find(({ Job_id }) => parseInt(Job_id) === parseInt(job.Job_id));

          //updating next job status to running
          if (updatedJob && !["running", "terminating"].includes(updatedJob.status)) {
            let nextId = null;
            if (updatedJob.NextJob_id && updatedJob.status === "success") nextId = updatedJob.NextJob_id;
            else if (updatedJob.ErrorJob_id && updatedJob.status !== "success") nextId = updatedJob.ErrorJob_id;

            const jobIndex = jobIndexMap[nextId];

            // Update status for next job on success or next job on error
            if (nextId && jobIndex !== undefined) {
              jobList[jobIndex].status = "running";
              jobList[jobIndex].statusText = "Running";
              jobList[jobIndex].StartTime = new Date().toISOString();
            }
          }

          return updatedJob || job;
        });
        setJobList(updatedJobList);
      }
    };

    const interval = setInterval(fetchData, 20000); // Update every 20 seconds

    return () => clearInterval(interval);
  }, [Workspace_id, selectedType, jobList, setJobList, getAllJobs, runningJobIds]);

  useLayoutEffect(() => {
    const storedValue = getStoredValue(`${User_id}_PipelineFilters`, {});

    const options = {
      orderBy: storedValue?.[Workspace_id]?.sortedOn?.orderBy,
      order: storedValue?.[Workspace_id]?.sortedOn?.order,
      search: storedValue?.[Workspace_id]?.search,
    };

    setOrderBy((prev) => options?.orderBy || prev);
    setOrder((prev) => options?.order || prev);
    setSearch((prev) => options?.search || prev);
    listingRef.current = { ...listingRef.current, ...options };

    setSelectedType(
      storedValue?.[Workspace_id]?.selectedType ||
        JOB_TYPES.filter((job) =>
          !IsDataVaultEnabled ? job.kmkEnabled || (!job.kmkEnabled && job.type === 1) : true
        ).map((job) => job.type)
    );
  }, [IsDataVaultEnabled, User_id, Workspace_id]);

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 5 }}>
        <Grid sx={{ mb: 1, display: "flex", alignItems: "flex-end", flexWrap: "wrap" }} size={12}>
          <Typography className={classes.HeaderTitle} component="div">
            Data Jobs
          </Typography>
          {[1, 2].includes(accessRole) ? (
            <ButtonUI
              variant="contained"
              color="info"
              className={classes.tableButton}
              sx={{ marginLeft: "0px !important" }}
              onClick={(e) => handleJobsMenuOpen(e)}
              endIcon={<AddIcon />}
            >
              Add Job
            </ButtonUI>
          ) : null}
          {!IsDataVaultEnabled && (
            <Box sx={{ display: "inline-flex", mb: "4px" }}>
              <HelpContextIcon screen="Data Pipelines" />
            </Box>
          )}
          <TextField
            data-id="userListSearch"
            margin="normal"
            value={search}
            onKeyDown={handleSearch}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            variant="standard"
            InputProps={{
              startAdornment: <SearchIcon color="primary" />,
              ...(search && {
                endAdornment: (
                  <IconButton onClick={handleClearSearch} size="small" sx={{ padding: 0 }}>
                    <ClearIcon color="primary" />
                  </IconButton>
                ),
              }),
            }}
            sx={{ margin: "0 20px 0 auto", width: "200px" }}
          />
          <MultiSelectDropDown
            dataid="JobTypeFilter"
            variant="standard"
            placeHolder={<MultiSelect title="Type" dataLength={selectedType.length} />}
            options={JOB_TYPES.filter((job) =>
              !IsDataVaultEnabled ? job.kmkEnabled || (!job.kmkEnabled && job.type === 1) : true
            )}
            selected={selectedType}
            basedOn="type"
            isSelectAll={true}
            onSelectChange={(selectionList) => handleTypeFilter(selectionList)}
          />
        </Grid>
        <Grid className={classes.BigTileContainer} size={12}>
          <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
            <KMKTable
              columns={columns}
              containerHeight={tableHeight}
              sortingProperties={{ orderBy, order, handleSorting }}
              paginationProperties={{}}
              noDataText="No data available"
              data={jobList}
              boldHeader
              tableProps={{ size: "medium", stickyHeader: true }}
              dataIdKey="App_id"
              onRowClick={handleRowClick}
            />
          </Paper>
        </Grid>
      </Grid>
      {openConfirm.open && (
        <ConfirmPopup
          configData={{
            popupTitle: PopupTitle[openConfirm.JobType]?.run || "",
            popupMessage: "Are you sure you want to continue?",
            handleCancel: () => setPrevState(setOpenConfirm, { open: false }),
            handleClick: () => handleJobRun(openConfirm.Job_id, openConfirm.JobType, true),
            loadingForConfirm: scheduleState.loadingForConfirm,
          }}
        ></ConfirmPopup>
      )}
      {openStopConfirm.open && (
        <ConfirmPopup
          configData={{
            popupTitle: `Terminate "${openStopConfirm?.JobName || ""}" Job`,
            popupMessage: "Are you sure you want to terminate this job?",
            handleCancel: () => setPrevState(setOpenStopConfirm, { open: false }),
            handleClick: () => handleStopJobExecution(openStopConfirm.Job_id, openStopConfirm.JobType),
            loadingForConfirm: scheduleState.loadingForConfirm,
          }}
        ></ConfirmPopup>
      )}
      {conflictConfirm.open && (
        <ConfirmPopup
          configData={{
            popupTitle: conflictConfirm.Title,
            popupMessage: conflictConfirm.message,
            handleCancel: () => setPrevState(setConflictConfirm, { open: false }),
            handleClick: () => handleJobRun(openConfirm.Job_id, openConfirm.JobType, false),
            confirmBtnName: "Run Anyway",
            loadingForConfirm: scheduleState.loadingForConfirm,
          }}
        ></ConfirmPopup>
      )}
      {historyPopup.open && (
        <JobHistory
          handleCancel={() => setPrevState(setHistoryPopup, { open: false })}
          popupTitle={`"${historyPopup.JobName}" History`}
          jobId={historyPopup.Job_id}
          jobType={historyPopup.JobType}
        />
      )}
      {errorLogPopup.open && DATA_MODELING_JOBS.includes(errorLogPopup.JobType) && (
        <DataLoadErrorLog
          configData={{
            open: errorLogPopup.open,
            handleCancel: () => setErrorLogPopup({ open: false, JobType: null, errors: "" }),
            popupTitle: PopupTitle[errorLogPopup.JobType]?.error || "",
            jobErrorList:
              errorLogPopup.errors?.length && typeof errorLogPopup.errors[0] === "string"
                ? errorLogPopup.errors.map((err) => ({ Stage: "", Message: err }))
                : errorLogPopup.errors,
          }}
        />
      )}
      {errorLogPopup.open && NON_MODELING_JOBS.includes(errorLogPopup.JobType) && (
        <ErrorLog
          open={errorLogPopup.open}
          handleCancel={() => setErrorLogPopup({ open: false, JobType: null, errors: "" })}
          popupTitle={PopupTitle[errorLogPopup.JobType].error}
          jobErrorList={errorLogPopup.errors}
        />
      )}
    </>
  );
};

export default memo(JobList);
